import {FC} from 'react';
//formik
import {Field, useFormikContext} from 'formik';
import {CreateEventFormValues} from '@/components/Organization/Dashboard/CreateNewEvent/formConfiguration';
//mui
import {Box, Typography} from '@mui/material';
//constants
import {FIELD, FIELD_LABEL, FORM, PLACEHOLDER, SUBTITLE1} from '@/utils/constants';
//icons
import {ReactComponent as LocationIcon} from '@/assets/icons/location.svg';
//helpers
import {setRequiredLabel} from '@/utils/helpers';
//components
import Input from '@/shared/components/Inputs/Input';
import DatePicker from '@/shared/components/DatePicker';
import TimePicker from '@/shared/components/TimePicker';
import Switch from '@/shared/components/Switches/Switch';
import GreyUploadImage from '@/shared/components/UploadDropzone/GreyUploadImage';
import ColorPicker from '@/shared/components/ColorPicker';
//styles
import {formContentStyle} from '@/components/Organization/Dashboard/CreateNewEvent/EventSetting/FormContent/FormContent.styles';
import FormHelperText from '@/shared/components/FormHelperText';
import CurrencyField from '@/shared/components/Inputs/CurrencyInput';
import {useAppSelector} from '@/store/hooks';
import {getTeamSelector} from '@/store/organization';
import {ITeamRole} from '@/utils/enums';

const EventSettingForm: FC = () => {
  const baseURL = process.env.REACT_APP_FE_URL;
  const team = useAppSelector(getTeamSelector);

  const isStandartMember = !!team && team.role === ITeamRole.STANDART;

  const {
    values: {name, isGoalAmount, description, logo},
    setFieldValue,
  } = useFormikContext<CreateEventFormValues>();

  const currentDescriptionLength = description.length;

  const handleUrlChange = (value: string) => {
    const processedValue = value.trim().toLowerCase().replace(/\s/g, '-').replace(/-{2,}/g, '-');
    setFieldValue('customizeWebpage', processedValue);
  };

  return (
    <Box component={FORM} sx={formContentStyle.wrapper}>
      <Box sx={formContentStyle.form}>
        <Box sx={formContentStyle.content}>
          <Box sx={formContentStyle.detailsWrapper}>
            <Typography
              variant={SUBTITLE1}
              sx={{...formContentStyle.title, ...(isStandartMember && {color: 'text.disabled'})}}
            >
              Event Details:
            </Typography>
            <Box sx={formContentStyle.urlWrapper}>
              <Box sx={formContentStyle.fieldHalf}>
                <Field
                  id={FIELD.name}
                  name={FIELD.name}
                  value={name}
                  placeholder={PLACEHOLDER.eventName}
                  label={setRequiredLabel(FIELD_LABEL.eventName)}
                  component={Input}
                  disabled={isStandartMember}
                />
              </Box>

              <Box sx={formContentStyle.fieldHalf}>
                <Field
                  id={FIELD.customizeWebpage}
                  name={FIELD.customizeWebpage}
                  label={FIELD_LABEL.customizeWebpage}
                  component={Input}
                  onChange={(value: string) => handleUrlChange(value)}
                  InputProps={{
                    startAdornment: <Box sx={formContentStyle.urlPrefix}>{baseURL}/</Box>,
                  }}
                  disabled={isStandartMember}
                />
              </Box>
            </Box>

            <Box sx={{position: 'relative'}}>
              <Field
                id={FIELD.description}
                name={FIELD.description}
                value={description}
                rows={6}
                sx={formContentStyle.description}
                placeholder={PLACEHOLDER.eventDescription}
                label={FIELD_LABEL.eventDescription}
                component={Input}
                multiline
                inputProps={{
                  maxLength: 500,
                }}
                disabled={isStandartMember}
              />
              <FormHelperText
                title={`${currentDescriptionLength}/500`}
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mt: 0.5,
                  color: 'text.secondary',
                  fontSize: '0.75rem', ...(isStandartMember && {color: 'text.disabled'})
                }}

              />
            </Box>
          </Box>
          <Box sx={formContentStyle.amount}>
            <Typography
              variant={SUBTITLE1}
              sx={{...formContentStyle.title, ...(isStandartMember && {color: 'text.disabled'})}}
            >
              Branding & Goal :
            </Typography>

            <Box sx={formContentStyle.brandingWrapper}>
              <Box sx={formContentStyle.logoWrapper}>
                <Field
                  id={FIELD.logo}
                  name={FIELD.logo}
                  label={setRequiredLabel(FIELD_LABEL.eventLogoOrImage)}
                  component={GreyUploadImage}
                  value={logo}
                  image={logo}
                  parentClassName={{
                    ...formContentStyle.logo,
                    ...(isStandartMember && {pointerEvents: 'none', opacity: 0.5}),
                  }}
                  disabled={isStandartMember}
                />
                <Typography sx={formContentStyle.logoHint}>Recommended size: 1200 × 600 px</Typography>
              </Box>

              <Box sx={formContentStyle.colorField}>
                <Field
                  id={FIELD.primaryColor}
                  name={FIELD.primaryColor}
                  label={FIELD_LABEL.primaryColor}
                  component={ColorPicker}
                  parentClassName={{
                    position: 'relative',
                    ...(isStandartMember && {pointerEvents: 'none'}),
                  }}
                  disabled={isStandartMember}
                />
              </Box>

              <Box sx={formContentStyle.goalRow}>
                <Box sx={formContentStyle.goalField}>
                  <CurrencyField
                    // id={FIELD.goalAmount}
                    name={FIELD.goalAmount}
                    placeholder={`$ ${PLACEHOLDER.zero}`}
                    label={FIELD_LABEL.goalAmount}
                    disabled={!isGoalAmount || isStandartMember}
                  />

                  <Typography sx={formContentStyle.goalHint}>Used to track progress on your dashboard.</Typography>
                </Box>

                <Field
                  id={FIELD.isGoalAmount}
                  name={FIELD.isGoalAmount}
                  sx={formContentStyle.goalSwitch}
                  component={Switch}
                  checked={isGoalAmount}
                  disabled={isStandartMember}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={formContentStyle.sceheduleWrapper}>
          <Typography
            variant={SUBTITLE1}
            sx={{...formContentStyle.title, ...(isStandartMember && {color: 'text.disabled'})}}
          >
            Schedule & Location:
          </Typography>
          <Box sx={formContentStyle.locationWrapper}>
            <Box sx={formContentStyle.dateWrapper}>
              <Box sx={formContentStyle.date}>
                <Field
                  id={FIELD.startDate}
                  name={FIELD.startDate}
                  label={setRequiredLabel(FIELD_LABEL.eventStartDate)}
                  component={DatePicker}
                  sx={formContentStyle.dateMonthYear}
                  disabled={isStandartMember}
                />
                <Field
                  id={FIELD.startTime}
                  name={FIELD.startTime}
                  label={setRequiredLabel(FIELD_LABEL.startTime)}
                  component={TimePicker}
                  parentClassName={formContentStyle.time}
                  disabled={isStandartMember}
                />
              </Box>
              <Box sx={formContentStyle.date}>
                <Field
                  id={FIELD.endDate}
                  name={FIELD.endDate}
                  label={setRequiredLabel(FIELD_LABEL.eventEndDate)}
                  component={DatePicker}
                  sx={formContentStyle.dateMonthYear}
                  disabled={isStandartMember}
                />
                <Field
                  id={FIELD.endTime}
                  name={FIELD.endTime}
                  label={setRequiredLabel(FIELD_LABEL.eventEndDate)}
                  component={TimePicker}
                  parentClassName={formContentStyle.time}
                  disabled={isStandartMember}
                />
              </Box>
            </Box>

            <Field
              id={FIELD.location}
              name={FIELD.location}
              placeholder={PLACEHOLDER.eventLocation}
              label={setRequiredLabel(FIELD_LABEL.eventLocation)}
              component={Input}
              InputProps={{startAdornment: <LocationIcon />}}
              disabled={isStandartMember}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EventSettingForm;
